import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { tx } from '../i18n';
import { pedir } from '../lib/api';
import { CANAL, estadoVivo, permisos, tonoDisco } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { hace, miles, pc } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import type { ApplianceFila, ApplianceLibre, Organizacion } from '../tipos';
import { CodigoDeAlta } from '../ui/CodigoDeAlta';
import { Cabecera, Consulta, Pastilla, primero, Tabla, useAviso, useRefrescar, Vacio, Ventana } from '../ui/piezas';

/** El alta: la organización y el nombre. El código sale después, una sola vez. */
function NuevoAppliance({
  organizaciones,
  alCerrar,
  alCrear,
}: {
  organizaciones: Organizacion[];
  alCerrar: () => void;
  alCrear: (codigo: string, nombre: string) => void;
}) {
  const [organizacion, setOrganizacion] = useState('');
  const [nombre, setNombre] = useState('');
  const [notas, setNotas] = useState('');
  return (
    <Ventana
      titulo={tx.appliances.nuevo}
      boton={tx.appliances.crearYCodigo}
      alCerrar={alCerrar}
      alEnviar={async () => {
        const falta = primero(!organizacion && tx.appliances.elijaOrganizacion, !nombre.trim() && tx.appliances.faltaNombre);
        if (falta) return falta;
        const r = await pedir<{ id: string; codigo: string }>('/appliances', {
          metodo: 'POST',
          cuerpo: { organizacion_id: organizacion, nombre: nombre.trim(), notas: notas.trim() || null },
        });
        alCrear(r.codigo, nombre.trim());
      }}
    >
      <label className="campo" htmlFor="nuevo-org">
        {tx.comun.organizacion}
        <select id="nuevo-org" className="lista-sel" value={organizacion} onChange={(e) => setOrganizacion(e.target.value)}>
          <option value="">{tx.comun.elegir}</option>
          {organizaciones.map((o) => (
            <option key={o.id} value={o.id}>
              {o.nombre}
            </option>
          ))}
        </select>
        {organizaciones.length === 0 && <small className="ayuda">{tx.appliances.sinOrganizaciones}</small>}
      </label>
      <label className="campo" htmlFor="nuevo-nombre">
        {tx.comun.nombre}
        <input
          id="nuevo-nombre"
          className="entrada"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={tx.appliances.nombreEjemplo}
        />
        <small className="ayuda">{tx.appliances.nombreAyuda}</small>
      </label>
      <label className="campo" htmlFor="nuevo-notas">
        {tx.appliances.notasOpcional}
        <textarea
          id="nuevo-notas"
          className="entrada"
          rows={3}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder={tx.appliances.notasEjemplo}
        />
      </label>
    </Ventana>
  );
}

/**
 * Asignar un libre: su propio estado, como el alta. Con el estado arriba, cada tecla pintaba
 * de nuevo la lista y la ventana devolvía el foco al primer campo.
 */
function AsignarLibre({
  libre,
  organizaciones,
  alCerrar,
  alAsignar,
}: {
  libre: ApplianceLibre;
  organizaciones: Organizacion[];
  alCerrar: () => void;
  alAsignar: (nombre: string, organizacion: string) => void;
}) {
  const t = tx.libres;
  const [organizacion, setOrganizacion] = useState('');
  const [nombre, setNombre] = useState(libre.nombre);
  return (
    <Ventana
      titulo={t.asignarTitulo(libre.nombre)}
      boton={t.asignar}
      alCerrar={alCerrar}
      alEnviar={async () => {
        const falta = primero(!organizacion && t.faltaOrganizacion, !nombre.trim() && tx.appliances.faltaNombre);
        if (falta) return falta;
        const r = await pedir<{ nombre: string; organizacion: string }>(`/appliances/${libre.id}/asignar`, {
          metodo: 'POST',
          cuerpo: { organizacion_id: organizacion, nombre: nombre.trim() },
        });
        alAsignar(r.nombre, r.organizacion);
      }}
    >
      <p className="d">{t.asignarQue}</p>
      <div className="dos-cols">
        <label className="campo" htmlFor="asignar-org">
          {tx.comun.organizacion}
          <select id="asignar-org" className="lista-sel" value={organizacion} onChange={(e) => setOrganizacion(e.target.value)}>
            <option value="">{tx.comun.elegir}</option>
            {organizaciones.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo" htmlFor="asignar-nombre">
          {tx.comun.nombre}
          <input id="asignar-nombre" className="entrada" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <small className="ayuda">{t.nombreAyuda}</small>
        </label>
      </div>
    </Ventana>
  );
}

/**
 * Los que se registraron solos apenas tuvieron internet: se reconocen por la huella que
 * muestra su pantalla, se les puede entrar por consola, y se asignan a una organización.
 */
function Libres({ organizaciones, avisar }: { organizaciones: Organizacion[]; avisar: (texto: string) => void }) {
  const { usuario } = useSesion();
  const opera = permisos(usuario?.rol).abrirRed;
  const q = useConsulta<ApplianceLibre[]>('/appliances/libres', { refetchInterval: 15_000 });
  const refrescar = useRefrescar();
  const [asignar, setAsignar] = useState<ApplianceLibre | null>(null);
  const [descartar, setDescartar] = useState<ApplianceLibre | null>(null);
  const t = tx.libres;
  if (!q.data?.length) return null;
  return (
    <section className="panel libres">
      <div className="libres-cab">
        <h2 className="rotulo">
          {t.titulo} <span className="cuenta">{q.data.length}</span>
        </h2>
        <p className="d">{t.que}</p>
      </div>
      <div className="libres-lista">
        {q.data.map((a) => (
          <article key={a.id} className="libre">
            <div className="libre-cab">
              <b>{a.nombre}</b>
              <Pastilla tono={a.en_linea ? 'arriba' : 'parcial'}>{a.en_linea ? t.enLinea : t.sinContacto}</Pastilla>
            </div>
            <div className="libre-huella" title={t.huella}>
              {a.huella}
            </div>
            <dl className="libre-datos">
              <div>
                <dt>{t.ip}</dt>
                <dd className="num">{a.ip ?? '—'}</dd>
              </div>
              <div>
                <dt>{t.version}</dt>
                <dd className="num">{a.version ?? '—'}</dd>
              </div>
              <div>
                <dt>{t.visto}</dt>
                <dd>{hace(a.visto)}</dd>
              </div>
            </dl>
            {opera && (
              <div className="libre-acc">
                <Link className="btn" to={`/appliances/${a.id}/consola?nombre=${encodeURIComponent(a.nombre)}`}>
                  {t.consola}
                </Link>
                <button type="button" className="btn prim" onClick={() => setAsignar(a)}>
                  {t.asignar}
                </button>
                <button type="button" className="btn fantasma" onClick={() => setDescartar(a)}>
                  {t.descartar}
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
      {asignar && (
        <AsignarLibre
          libre={asignar}
          organizaciones={organizaciones}
          alCerrar={() => setAsignar(null)}
          alAsignar={(nombre, organizacion) => {
            setAsignar(null);
            void refrescar('/appliances/libres', '/appliances', '/organizaciones', '/resumen');
            avisar(t.asignado(nombre, organizacion));
          }}
        />
      )}
      {descartar && (
        <Ventana
          titulo={t.descartarTitulo(descartar.nombre)}
          boton={t.descartar}
          peligro
          alCerrar={() => setDescartar(null)}
          alEnviar={async () => {
            await pedir(`/appliances/${descartar.id}/descartar`, { metodo: 'POST' });
            setDescartar(null);
            void refrescar('/appliances/libres');
            avisar(t.descartado(descartar.nombre));
          }}
        >
          <p>{t.descartarQue}</p>
        </Ventana>
      )}
    </section>
  );
}

export function Appliances() {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  const [params, setParams] = useSearchParams();
  const [nuevo, setNuevo] = useState(false);
  const [codigo, setCodigo] = useState<{ codigo: string; nombre: string } | null>(null);
  const [aviso, avisar] = useAviso();
  const refrescar = useRefrescar();

  const organizacion = params.get('organizacion') ?? '';
  const estado = params.get('estado') ?? '';
  const q = params.get('q') ?? '';
  // Los filtros viven en la dirección: una lista filtrada se puede pegar en un chat.
  const poner = (clave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(clave, valor);
    else p.delete(clave);
    setParams(p, { replace: true });
  };

  const organizaciones = useConsulta<Organizacion[]>('/organizaciones');
  // El filtrado lo hace la API: así la lista no miente cuando hay más de los que llegaron.
  const lista = useConsulta<ApplianceFila[]>('/appliances', { refetchInterval: 60_000 }, { organizacion, estado, q });
  const filas = lista.data ?? [];

  return (
    <div className="pagina">
      <Cabecera
        titulo={tx.comun.appliances}
        accion={
          puede.crearAppliance && (
            <button type="button" className="btn prim" onClick={() => setNuevo(true)}>
              {tx.appliances.nuevo}
            </button>
          )
        }
      >
        {tx.appliances.que}
      </Cabecera>
      {aviso}

      <Libres organizaciones={organizaciones.data ?? []} avisar={avisar} />

      <div className="filtros">
        <label className="solo-lector" htmlFor="f-org">
          {tx.comun.organizacion}
        </label>
        <select id="f-org" className="lista-sel" value={organizacion} onChange={(e) => poner('organizacion', e.target.value)}>
          <option value="">{tx.appliances.todasLasOrganizaciones}</option>
          {(organizaciones.data ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.nombre}
            </option>
          ))}
        </select>
        <label className="solo-lector" htmlFor="f-estado">
          {tx.appliances.estado}
        </label>
        <select id="f-estado" className="lista-sel" value={estado} onChange={(e) => poner('estado', e.target.value)}>
          <option value="">{tx.appliances.todosLosEstados}</option>
          <option value="activo">{tx.appliances.activo}</option>
          <option value="alta">{tx.appliances.esperandoAlta}</option>
          <option value="suspendido">{tx.appliances.suspendido}</option>
          <option value="revocado">{tx.appliances.revocado}</option>
        </select>
        <label className="solo-lector" htmlFor="f-buscar">
          {tx.comun.buscar}
        </label>
        <input
          id="f-buscar"
          className="entrada buscar"
          type="search"
          placeholder={tx.appliances.buscarEjemplo}
          value={q}
          onChange={(e) => poner('q', e.target.value)}
        />
      </div>

      <section className="panel">
        <Consulta
          q={lista}
          vacio={
            lista.data && filas.length === 0 ? (
              <Vacio>{organizacion || estado || q ? tx.appliances.nadaConEsoFiltros : tx.appliances.ningunoTodavia}</Vacio>
            ) : undefined
          }
        >
          <div className="scroll-x">
            <Tabla className="tabla-appliances">
              <thead>
                <tr>
                  <th>{tx.comun.appliance}</th>
                  <th>{tx.appliances.estado}</th>
                  <th>{tx.comun.version}</th>
                  <th className="der">{tx.comun.equipos}</th>
                  <th className="der">{tx.appliances.caidos}</th>
                  <th className="der">{tx.comun.disco}</th>
                  <th>{tx.comun.ultimoContacto}</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((a) => {
                  const e = estadoVivo(a);
                  return (
                    <tr key={a.id}>
                      <td>
                        <Link className="nombre" to={`/appliances/${a.id}`}>
                          {a.nombre}
                        </Link>
                        <small className="d">{a.organizacion}</small>
                      </td>
                      <td>
                        <Pastilla tono={e.tono}>{e.texto}</Pastilla>
                      </td>
                      <td className="num">
                        {a.version ?? '—'}
                        <small className="d">{`${CANAL[a.canal] ?? a.canal} ${tx.comun.canal}`}</small>
                      </td>
                      <td className="der num">{miles(a.equipos)}</td>
                      <td className={`der num${a.equipos_caidos > 0 ? ' mal' : ''}`}>{miles(a.equipos_caidos)}</td>
                      <td className={`der num disco-${tonoDisco(a.disco_pc)}`}>{pc(a.disco_pc)}</td>
                      <td className="d">{hace(a.visto)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabla>
          </div>
        </Consulta>
      </section>

      {nuevo && (
        <NuevoAppliance
          organizaciones={organizaciones.data ?? []}
          alCerrar={() => setNuevo(false)}
          alCrear={(c, nombre) => {
            setNuevo(false);
            setCodigo({ codigo: c, nombre });
            void refrescar('/appliances', '/organizaciones', '/resumen');
            avisar(tx.appliances.creado(nombre));
          }}
        />
      )}
      {codigo && <CodigoDeAlta codigo={codigo.codigo} nombre={codigo.nombre} alCerrar={() => setCodigo(null)} />}
    </div>
  );
}
